<script setup>
import FilterItem from "~/components/molecules/FilterItem.vue";
import Image from "~/components/atoms/Image.vue";
import Checkbox from "~/components/atoms/Checkbox.vue";
import Text from "~/components/atoms/Text.vue";
import RangeFilter from "~/components/molecules/RangeFilter.vue";

const props = defineProps({
  filterItem: Object,
  chosenFilters: Object,
  absoluteRanges: {
    type: Object,
    default: () => ({})
  }
});
const emit = defineEmits(['click', 'range-change']);

const rangeMin = computed(() => {
  if (!props.filterItem?.values?.length) return 0
  return Math.min(...props.filterItem.values.map(v => parseFloat(v.value) || 0))
})

const rangeMax = computed(() => {
  if (!props.filterItem?.values?.length) return 100
  return Math.max(...props.filterItem.values.map(v => parseFloat(v.value) || 0))
})

const absoluteMin = computed(() => {
  const abs = props.absoluteRanges?.[props.filterItem?.slug]
  return abs ? abs.min : rangeMin.value
})

const absoluteMax = computed(() => {
  const abs = props.absoluteRanges?.[props.filterItem?.slug]
  return abs ? abs.max : rangeMax.value
})


const currentRange = computed(() => {
  const val = props.chosenFilters?.[props.filterItem?.slug]
  if (val && typeof val === 'string' && val.includes('_')) {
    const [from, to] = val.split('_').map(Number)
    return { from, to }
  }
  return { from: absoluteMin.value, to: absoluteMax.value }
})

const onRangeChange = ({ from, to }) => {
  const isDefault = from === absoluteMin.value && to === absoluteMax.value
  emit('range-change', { name: props.filterItem.slug, value: isDefault ? null : `${from}_${to}` })
}

const filterItem = ref(props.filterItem);
const emitClick = (name, value) => {
  emit('click', {name, value})
}
</script>

<template>
  <template v-if="filterItem.type === 'range'">
    <RangeFilter
      :min="Number(absoluteMin)"
      :max="Number(absoluteMax)"
      :from="Number(currentRange.from)"
      :to="Number(currentRange.to)"
      @change="onRangeChange"
    />
  </template>
  <template v-else-if="filterItem.is_text">
    <div class="text-item row full-width space-between clickable" v-for="value in filterItem.values">
      <div class="row g-8 full-width">
        <Checkbox :checked="chosenFilters[filterItem.slug]?.includes(value.slug)"
                  @change="emitClick(filterItem.slug,value.slug)" :text="value.value"/>
      </div>

      <Text type="counter">{{ value.count }}</Text>
    </div>
  </template>
  <template v-else-if="filterItem.slug === 'color'">
    <div class="filter-grid row full-width" v-for="colors in filterItem.values">
      <FilterItem :active="!!chosenFilters[filterItem.slug]?.includes(color.slug)"
                  @click="emitClick(filterItem.slug,color.slug)" :count="color.count" :name="color.value"
                  v-for="color in colors">
        <div class="circle" :class="color.value" :style="{background:color.color}">
        </div>
      </FilterItem>
    </div>
  </template>
  <template v-else-if="filterItem.slug === 'size'">
    <div class="filter-grid row full-width" data-filter="size">
      <template v-for="size in filterItem.values">
        <FilterItem :data-slug="size.slug" :active="!!chosenFilters[filterItem.slug]?.includes(size.slug)" :count="size.count"
                    @click="emitClick(filterItem.slug,size.slug)" :name="size.value">
          <div class="circle" :class="filterItem.slug">
            <div class="icon" v-if="size.icon">
              <img :src="'https://hillceramic.se/'+size.icon"/>
            </div>
          </div>
        </FilterItem>

        <template v-if="size.values?.length" v-for="underSize in size?.values">
          <div class="last-sizes">
            <FilterItem :data-slug="underSize.slug" :data-group="size.slug" :active="!!chosenFilters[filterItem.slug]?.includes(underSize.slug)" :count="underSize.count"
                        @click="emitClick(filterItem.slug,underSize.slug)"
                        :name="underSize.value">
              <div class="circle" :class="filterItem.slug">
                <div class="icon" v-if="underSize.icon">
                  <img :src="'https://hillceramic.se/'+underSize.icon"/>
                </div>
              </div>
            </FilterItem>

            <div v-if="underSize.values?.length" class="last-size text-center clickable">
              <Text :class="{active:!!chosenFilters[filterItem.slug]?.includes(lastSize.slug)}"
                    :data-group="size.slug" :data-sub-group="underSize.slug" :data-slug="lastSize.slug"
                    @click="emitClick(filterItem.slug,lastSize.slug)" v-for="lastSize in underSize.values">
                {{ lastSize.value }}
              </Text>
            </div>
          </div>
        </template>
      </template>
    </div>
  </template>
  <template v-else>
    <div class="filter-grid row full-width">
      <FilterItem :active="!!chosenFilters[filterItem.slug]?.includes(item.slug)" :count="item.count"
                  @click="emitClick(filterItem.slug,item.slug)" :name="item.value"
                  v-for="item in filterItem.values">
        <div class="circle" :class="filterItem.slug">
          <div class="icon" v-if="item.icon">
            <Image :src="'https://hillceramic.se/'+item.icon"/>
          </div>
        </div>
      </FilterItem>
    </div>
  </template>
</template>

<style scoped lang="scss">
.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 4fr);
  row-gap: 18px;
  align-items: flex-start;
}

.circle {
  width: 52px;
  height: 52px;
  max-width: 52px;
  max-height: 52px;
  margin: auto;
  background: $grey-green;
  overflow: hidden;
  padding: 10px;
}

.Vit {
  border: 1px solid;
}

.textur {
  padding: 0;
}

.counter {
  font-size: 12px;
  color: $light-black;
}

.last-sizes {
  margin: 0 auto;
}


.last-size {
  margin-top: 14px;

  p {
    width: 56px;
    box-sizing: border-box;
    height: 23px;
    margin: auto;
    font-size: 11px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover, &.active {
      border: 1px solid $black;
      border-radius: 5px;
    }
  }
}

</style>
